const CLARITY_PROJECT_ID = "xxajqokqgk";

/**
 * Scroll behaviour on route change. Applies to every page — the homepage,
 * /projects/ and each /projects/<slug>/.
 *
 * Without this, navigating from the homepage portfolio section (which is far
 * down the page) carried the old scroll offset over and left you near the
 * bottom of the new page.
 *
 * We scroll ourselves inside requestAnimationFrame and return false, rather
 * than letting Gatsby do it: Gatsby scrolls as soon as the route changes,
 * which can land before the new page has laid out. Doing it after the next
 * frame means the document is the right height when we move.
 */
exports.shouldUpdateScroll = ({
  routerProps: { location },
  getSavedScrollPosition,
}) => {
  // In-page anchors (e.g. /#about) must be left alone or they cannot scroll
  // to their target.
  if (location.hash) return true;

  // A saved position only exists for history entries the user has actually
  // visited, so: back/forward restores, a fresh navigation goes to the top.
  const target = getSavedScrollPosition(location) || [0, 0];

  window.requestAnimationFrame(() => {
    window.scrollTo({ left: target[0], top: target[1], behavior: "instant" });
  });

  return false;
};

/**
 * Microsoft Clarity.
 *
 * Loaded on idle rather than inlined in <head>. The tag is async either way,
 * but injecting it during initial load still makes it compete for bandwidth
 * with the hero image and fonts — the two things we just optimised for LCP.
 * Clarity backfills the session once it initialises, so deferring costs no
 * data.
 *
 * Uses Clarity's own snippet rather than the @microsoft/clarity npm package:
 * the package is a thin wrapper whose extra API (identify, consent, upgrade)
 * we do not use, so it would add a dependency and bundle weight for nothing.
 * Switch to it if you ever need those calls.
 */
const loadClarity = () => {
  if (window.clarity) return; // already initialised
  // Clarity's own snippet, kept verbatim so it stays easy to diff against
  // whatever their dashboard hands you next.
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_PROJECT_ID);
};

exports.onInitialClientRender = () => {
  // Never in development — it would pollute the recordings with localhost
  // sessions and your own dev traffic.
  if (process.env.NODE_ENV !== "production") return;

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(loadClarity, { timeout: 5000 });
  } else {
    // Safari has no requestIdleCallback.
    window.setTimeout(loadClarity, 2500);
  }
};
